import { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Link2, Upload, X } from 'lucide-react';
import { useAllDossierFiles } from '../../hooks/queries/useDossierFiles';
import { taskSecondaryAccordionLabel } from '../../lib/tasks/taskSecondaryAccordion';
import {
  taskSecondaryQueueCount,
  type TaskSecondaryQueue,
} from '../../lib/tasks/flushTaskSecondaryQueue';
import { Textarea } from '../ui/textarea';

interface Props {
  dossierId?: string | null;
  queue: TaskSecondaryQueue;
  onChange: (queue: TaskSecondaryQueue) => void;
}

export function TaskSecondaryPendingAccordion({ dossierId, queue, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: dossierFiles = [] } = useAllDossierFiles(dossierId ?? undefined);
  const count = taskSecondaryQueueCount(queue);
  const label = taskSecondaryAccordionLabel(count);
  const linked = new Set(queue.dossierFileIds);

  return (
    <div className="pt-2 border-t border-neutral-100">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-cime-text-muted hover:text-cime-text-primary"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        <span>{label}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-4">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
                Pièces jointes
                {queue.files.length > 0 && (
                  <span className="ml-1.5 font-normal">({queue.files.length})</span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-xs text-cime-text-muted hover:border-cime-primary/30 hover:bg-neutral-50"
              >
                <Upload className="h-3.5 w-3.5" />
                Ajouter
              </button>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onChange({ ...queue, files: [...queue.files, file] });
                  e.target.value = '';
                }}
              />
            </div>
            {queue.files.length === 0 ? (
              <p className="text-xs italic text-cime-text-muted">Aucune pièce jointe.</p>
            ) : (
              <ul className="space-y-1">
                {queue.files.map((file, i) => (
                  <li
                    key={`${file.name}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      aria-label="Retirer"
                      onClick={() =>
                        onChange({
                          ...queue,
                          files: queue.files.filter((_, idx) => idx !== i),
                        })
                      }
                      className="text-neutral-400 hover:text-cime-text-primary"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {dossierId && (
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium text-neutral-700">Fichiers du dossier</h3>
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-cime-primary hover:underline"
                >
                  <Link2 className="h-3.5 w-3.5" /> Joindre un fichier
                </button>
              </div>
              {queue.dossierFileIds.length === 0 && !pickerOpen && (
                <p className="text-xs text-neutral-400">Aucun fichier du dossier lié à cette tâche.</p>
              )}
              {pickerOpen && (
                <ul className="mb-2 max-h-40 space-y-1 overflow-y-auto rounded-md border border-neutral-200 p-2">
                  {dossierFiles.length === 0 && (
                    <li className="text-xs text-cime-text-muted">Aucun fichier dans le dossier.</li>
                  )}
                  {dossierFiles.map((file) => {
                    const on = linked.has(file.id);
                    return (
                      <li key={file.id}>
                        <button
                          type="button"
                          onClick={() =>
                            onChange({
                              ...queue,
                              dossierFileIds: on
                                ? queue.dossierFileIds.filter((id) => id !== file.id)
                                : [...queue.dossierFileIds, file.id],
                            })
                          }
                          className={`w-full truncate rounded px-2 py-1 text-left text-xs ${
                            on ? 'bg-cime-primary/10 text-cime-primary' : 'hover:bg-neutral-50'
                          }`}
                        >
                          {file.filename}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {queue.dossierFileIds.length > 0 && (
                <p className="text-xs text-cime-text-muted">
                  {queue.dossierFileIds.length} fichier
                  {queue.dossierFileIds.length > 1 ? 's' : ''} à lier à la création.
                </p>
              )}
            </section>
          )}

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-cime-text-muted">
              Commentaires
            </h3>
            <Textarea
              value={queue.comment}
              onChange={(e) => onChange({ ...queue, comment: e.target.value })}
              placeholder="Premier commentaire (optionnel)…"
              rows={3}
              className="resize-none"
            />
          </section>
        </div>
      )}
    </div>
  );
}
